import { useState } from "react";
import { Link } from "react-router-dom";
import { speakUpClient } from "../apiClient/speakUpClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { useAuth } from "../../gym/auth/useAuth";
import { CheckIcon, EyeOffIcon, UserIcon } from "../components/icons";

const MAX_MESSAGE_LENGTH = 4000;
const NEAR_LIMIT_REMAINING = 200;

export function SpeakUpPage() {
  const { isAdmin, principal } = useAuth();
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentAnonymously, setSentAnonymously] = useState<boolean | null>(null);

  async function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed) {
      setError("Write a message first.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await speakUpClient.submit(trimmed, isAnonymous);
      setMessage("");
      setSentAnonymously(isAnonymous);
      setIsAnonymous(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send this — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const remaining = MAX_MESSAGE_LENGTH - message.length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Speak Up</h1>
          <p className="page-sub">Tell us anything — a concern, an idea, a compliment. Your choice whether we know it&rsquo;s from you.</p>
        </div>
        {isAdmin && (
          <Link to="/speakup/admin" className="btn btn-ghost">
            Manage submissions
          </Link>
        )}
      </header>

      <div className="form-card">
        {sentAnonymously !== null ? (
          <div className="speakup-success">
            <span className="speakup-success-icon">
              <CheckIcon />
            </span>
            <h2>Thanks — this has been sent.</h2>
            <p className="page-sub">Your feedback was submitted{sentAnonymously ? " anonymously" : ""}.</p>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setSentAnonymously(null)}>
                Send another
              </button>
            </div>
          </div>
        ) : (
          <div className="form">
            <div className="form-field">
              <label htmlFor="speakup-message">Your message</label>
              <textarea
                id="speakup-message"
                className="speakup-textarea"
                rows={7}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="What's on your mind?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <span className={`speakup-char-count${remaining <= NEAR_LIMIT_REMAINING ? " is-near-limit" : ""}`}>
                {message.length} / {MAX_MESSAGE_LENGTH}
              </span>
            </div>

            <fieldset className="form-field">
              <legend>Attribution</legend>
              <div className="attribution-choices">
                <button
                  type="button"
                  className={`attribution-choice${isAnonymous ? " is-selected" : ""}`}
                  aria-pressed={isAnonymous}
                  onClick={() => setIsAnonymous(true)}
                >
                  <span className="attribution-choice-icon">
                    <EyeOffIcon />
                  </span>
                  <span className="attribution-choice-text">
                    <span className="attribution-choice-title">Stay anonymous</span>
                    <span className="attribution-choice-sub">Hidden from reviewers</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`attribution-choice${!isAnonymous ? " is-selected" : ""}`}
                  aria-pressed={!isAnonymous}
                  onClick={() => setIsAnonymous(false)}
                >
                  <span className="attribution-choice-icon">
                    <UserIcon />
                  </span>
                  <span className="attribution-choice-text">
                    <span className="attribution-choice-title">Show my name</span>
                    <span className="attribution-choice-sub">{principal?.userDetails}</span>
                  </span>
                </button>
              </div>
              <span className="field-hint">Anonymous hides your name from whoever reviews this.</span>
            </fieldset>

            {error && <div className="form-banner form-banner--error">{error}</div>}

            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Sending…" : "Send feedback"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
